import type Luminary from '@lumen5/luminary';
import type { Asset, Scene, SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

import type {
    DecisionChoiceAnswer,
    DecisionChoiceQuestion,
} from 'js/src/agent/decisions/decisionsClient';
import { requestDecisions } from 'js/src/agent/decisions/decisionsClient';
import {
    appearsAtInScene,
    LATE_CONTENT_THRESHOLD_MS,
} from 'js/src/agent/lint/checks/lateSceneContent';
import {
    editableContentAssets,
    hasContent,
    isMotionGraphicsScene,
} from 'js/src/agent/lint/checks/sceneContent';
import {
    isTooBriefToRead,
    revealedTextVisibleMs,
    videoReadingSpeed,
} from 'js/src/agent/lint/checks/voicedrivenSceneReadingTime';
import { getSceneDesignDefaults } from 'js/src/agent/lint/sceneDesignDefaults';
import { TOOL_NAMES } from 'js/src/agent/toolNames';

import {
    applyResolvedAssetTimingOperation,
    resolveAssetTimingOperation,
} from './asset-timing-placement';
import { isHtmlGraphicScene } from '../rendering/sceneScreenshot';

import { sceneDisplayId } from './scene-addressing';
import { partitionTranscriptByScene } from './transcript-partition';
import { type InterleavedWord, interleavedTokenToString } from './transcript-tokens';

import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { getRootAssetGroup, isVoiceDrivenSceneGroup } from 'js/src/luminary/luminary-selectors';

// Words either side of each option, so a word the narration repeats ("is", "your") can be told
// apart. One each side scored as well as three on the lint-asset-timing eval.
const CONTEXT_WORDS = 1;

// Each question carries its scene's words as options (~350 tokens); this keeps a request to a
// modest size. Longer videos split into parallel requests.
const MAX_QUESTIONS_PER_REQUEST = 60;

// Slack (ms) when comparing an asset's offset to its design default, so float noise isn't a retime.
const DEFAULT_OFFSET_EPSILON_MS = 1;

// A recommended start this close to where the asset already appears is left as it is.
const UNCHANGED_TOLERANCE_MS = 100;

/** One asset auto-timing moved, phrased for the lint report. */
export interface AutoTimedAsset {
    sceneId: string;
    assetName: string;
    startWords: string[];
}

/**
 * One asset auto-timing left where it was although the model picked a word for it, because landing
 * it there would trip another lint check. `reason` reads on from "left where it is: ".
 */
export interface HeldAsset {
    sceneId: string;
    assetName: string;
    reason: string;
}

export interface AutoTimingResult {
    timed: AutoTimedAsset[];
    /** Only those held on a fresh answer, so a repeat lint doesn't say the same again. */
    held: HeldAsset[];
}

interface Candidate {
    scene: Scene;
    asset: Asset;
    /** The scene's spoken words: what the model chooses between. */
    words: InterleavedWord[];
    /** The scene's words and pauses, which start_words must spell out. */
    tokens: InterleavedWord[];
    /** An overlay over the narration, as opposed to the group's own presenter or waveform scene. */
    isOverlay: boolean;
}

const isTimeable = (asset: Asset): boolean =>
    (VideoData.isTextAsset(asset) || VideoData.isIconAsset(asset)) &&
    hasContent(asset) &&
    !asset.isMatchCutFrozen &&
    !(VideoData.isSubscene(asset.luminaryParent) && asset.luminaryParent.index > 0);

/**
 * An asset still sitting at its scene design's intro offset has never been timed — not by the
 * agent's set_text/set_icon, not by a person in the editor, and not by an earlier auto-timing pass.
 * Only those are auto-timed, so deliberate timing is never overwritten and re-linting is a no-op.
 */
function hasDesignDefaultTiming(asset: Asset, designOffsets: Map<string, number>): boolean {
    const designOffset = designOffsets.get(asset.name) ?? 0;
    return Math.abs(asset.introAnimationOffset - designOffset) <= DEFAULT_OFFSET_EPSILON_MS;
}

/** What an icon from our library carries beyond Luminary's IconSourceData: copied from its library entry. */
interface LibraryIconSource {
    title?: string;
    description?: string | null;
    tags?: string[];
}

/**
 * What the asset shows, in words the model can match against the narration. An icon is described
 * by the library entry it was placed from (title, description and tags — library titles are often
 * just a pack position like "Business5", so the tags carry the meaning), plus any labels it is
 * grouped with.
 */
function describeAsset(asset: Asset): string {
    if (VideoData.isTextAsset(asset)) {
        return `on-screen text "${asset.text.trim()}"`;
    }
    const library = (VideoData.isIconAsset(asset) ? asset.sourceData : {}) as LibraryIconSource;
    const details = [
        library.description,
        library.tags?.length ? `tags: ${library.tags.join(', ')}` : null,
    ]
        .filter(Boolean)
        .join('; ');
    const depicts = library.title
        ? `an icon of "${library.title}"${details ? ` (${details})` : ''}`
        : 'an icon';
    const labels = (getRootAssetGroup(asset)?.getAssets() ?? [])
        .filter((child) => VideoData.isTextAsset(child) && child.text.trim())
        .map((child) => `"${(child as { text: string }).text.trim()}"`);
    if (labels.length > 0) {
        return `${depicts} illustrating ${labels.join(' / ')}`;
    }
    return library.title ? depicts : 'a decorative icon';
}

/**
 * The shortest run from the picked word that occurs only once in the scene, as start_words takes it.
 * Built over words and pauses alike, since a pause inside the run must be named in it.
 */
function uniqueStartWords(tokens: InterleavedWord[], word: InterleavedWord): string[] {
    const notation = tokens.map(interleavedTokenToString);
    const start = tokens.indexOf(word);
    for (let end = start + 1; end <= notation.length; end++) {
        // A run ends on a spoken word, never on a pause.
        if (tokens[end - 1].isPause) {
            continue;
        }
        const run = notation.slice(start, end);
        const occurrences = notation.filter((_, from) =>
            run.every((token, offset) => notation[from + offset] === token),
        ).length;
        if (occurrences === 1) {
            return run;
        }
    }
    return notation.slice(start);
}

/** Each word as the model sees it: bracketed, with its neighbours. */
function wordOptions(words: InterleavedWord[]): string[] {
    return words.map((word, index) => {
        const before = words.slice(Math.max(0, index - CONTEXT_WORDS), index);
        const after = words.slice(index + 1, index + 1 + CONTEXT_WORDS);
        return [...before.map((w) => w.word), `[${word.word}]`, ...after.map((w) => w.word)].join(
            ' ',
        );
    });
}

function wordQuestion(
    luminary: Luminary,
    name: string,
    { scene, asset, words }: Candidate,
): DecisionChoiceQuestion {
    const choices = wordOptions(words).map((description, index) => ({
        value: String(index),
        description,
    }));
    return {
        type: 'choice',
        name,
        instructions:
            `Scene ${sceneDisplayId(luminary, scene.id)}: ${describeAsset(asset)} appears on ` +
            "screen during this scene's narration. On which word should it first appear? Pick " +
            'the word where the narrator starts talking about what it shows.',
        choices,
    };
}

const HEADING = 'heading';
const NO_TEXT = 'none';

/**
 * Asked apart from the word question: a heading belongs at the scene start even when the narration
 * says its words later, and folding that rule into the word question made the model place the other
 * texts worse on the eval.
 */
function roleQuestion(luminary: Luminary, name: string, { scene, asset }: Candidate) {
    return {
        type: 'choice' as const,
        name,
        instructions: `Scene ${sceneDisplayId(luminary, scene.id)}: what is ${describeAsset(asset)} on this scene?`,
        choices: [
            {
                value: 'point',
                description:
                    'A point the narration reaches partway through: a list item, a stat, a label, a caption.',
            },
            {
                value: HEADING,
                description:
                    "The scene's heading: a title that introduces the whole scene, or a lower third naming the speaker.",
            },
        ],
    };
}

/** The texts on the asset's scene an icon could illustrate, keyed by asset name. */
function sceneTexts(scene: Scene): Asset[] {
    return (editableContentAssets(scene) as unknown as Asset[]).filter(
        (asset) => VideoData.isTextAsset(asset) && asset.text.trim(),
    );
}

/** Which text an icon illustrates, so it can land with that text rather than on a word of its own. */
function pairingQuestion(
    luminary: Luminary,
    name: string,
    { scene, asset }: Candidate,
): DecisionChoiceQuestion {
    return {
        type: 'choice',
        name,
        instructions: `Scene ${sceneDisplayId(luminary, scene.id)}: ${describeAsset(asset)} is on screen with these texts. Which one does it illustrate?`,
        choices: [
            { value: NO_TEXT, description: 'None of them: it stands on its own.' },
            ...sceneTexts(scene).map((text) => ({
                value: text.name,
                description: `"${(text as { text: string }).text.trim()}"`,
            })),
        ],
    };
}

interface Answers {
    word?: DecisionChoiceAnswer;
    /** For a text, its role; for an icon, the name of the text it illustrates. */
    relation?: DecisionChoiceAnswer;
}

/** Every untimed text and icon on a voice-driven scene, with the words its scene narrates. */
function collectCandidates(
    luminary: Luminary,
    getState: GetRootState,
    ownsSceneGroup: (sceneGroup: SceneGroup) => boolean,
): Candidate[] {
    const state = getState();
    return luminary.sceneGroups
        .filter((sceneGroup) => isVoiceDrivenSceneGroup(sceneGroup) && ownsSceneGroup(sceneGroup))
        .flatMap((sceneGroup) => partitionTranscriptByScene(state, sceneGroup) ?? [])
        .flatMap(({ scene, tokens, role }) => {
            // An HTML graphic animates its own contents, so its assets aren't ours to time.
            if (isHtmlGraphicScene(scene)) {
                return [];
            }
            const words = tokens.filter((token) => !token.isPause);
            const designOffsets = getSceneDesignDefaults(scene, (node) =>
                typeof node.introAnimationOffset === 'number'
                    ? node.introAnimationOffset
                    : undefined,
            );
            if (words.length === 0 || !designOffsets) {
                return [];
            }
            return (editableContentAssets(scene) as unknown as Asset[])
                .filter(
                    (asset) => isTimeable(asset) && hasDesignDefaultTiming(asset, designOffsets),
                )
                .map((asset) => ({ scene, asset, words, tokens, isOverlay: role === 'overlay' }));
        });
}

/**
 * What the decision model answered for each asset, per video, keyed by everything its questions are
 * built from. A repeat lint only asks about assets whose text, icon, scene texts or narration have
 * changed since: one it chose to leave where it was (a heading, a pick that matched, a word that
 * would trip another check) isn't asked again, and maybe moved, on every lint.
 */
const answersByVideo = new WeakMap<Luminary, Map<string, Answers>>();

function decisionKey({ scene, asset, words }: Candidate): string {
    return JSON.stringify([
        asset.id,
        describeAsset(asset),
        words.map((word) => word.word),
        VideoData.isIconAsset(asset)
            ? sceneTexts(scene).map((text) => [text.name, (text as { text: string }).text])
            : null,
    ]);
}

/** Every candidate's answers, asking the model only about those it hasn't already answered. */
async function askDecisionModel(
    luminary: Luminary,
    candidates: Candidate[],
): Promise<{ answers: Answers[]; asked: Set<Asset> }> {
    const remembered = answersByVideo.get(luminary) ?? new Map<string, Answers>();
    answersByVideo.set(luminary, remembered);
    const unasked = candidates.filter((candidate) => !remembered.has(decisionKey(candidate)));

    const chunks: Candidate[][] = [];
    for (let i = 0; i < unasked.length; i += MAX_QUESTIONS_PER_REQUEST) {
        chunks.push(unasked.slice(i, i + MAX_QUESTIONS_PER_REQUEST));
    }
    await Promise.all(
        chunks.map(async (chunk) => {
            const scenes = [...new Map(chunk.map((c) => [c.scene.id, c])).values()];
            const { answers } = await requestDecisions(
                JSON.stringify({
                    scenes: scenes.map(({ scene, words }) => ({
                        scene: sceneDisplayId(luminary, scene.id),
                        narration: words.map((word) => word.word).join(' '),
                        on_screen: chunk
                            .filter((c) => c.scene === scene)
                            .map((c) => describeAsset(c.asset)),
                    })),
                }),
                chunk.flatMap((candidate, index) => [
                    wordQuestion(luminary, `w${index}`, candidate),
                    VideoData.isTextAsset(candidate.asset)
                        ? roleQuestion(luminary, `r${index}`, candidate)
                        : pairingQuestion(luminary, `r${index}`, candidate),
                ]),
            );
            // A refused question leaves its asset untimed, the same as an unanswered one.
            const answerByName = new Map(
                answers
                    .filter((answer): answer is DecisionChoiceAnswer => answer.type === 'choice')
                    .map((answer) => [answer.name, answer]),
            );
            chunk.forEach((candidate, index) => {
                remembered.set(decisionKey(candidate), {
                    word: answerByName.get(`w${index}`),
                    relation: answerByName.get(`r${index}`),
                });
            });
        }),
    );
    return {
        answers: candidates.map((candidate) => remembered.get(decisionKey(candidate)) ?? {}),
        asked: new Set(unasked.map(({ asset }) => asset)),
    };
}

const seconds = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

/** Where an asset would land on the words picked for it, before anything is written. */
interface Landing {
    candidate: Candidate;
    startWords: string[];
    /** Milliseconds into the asset's container. */
    introTime: number;
}

/**
 * Times every not-yet-timed text and icon on the video's voice-driven scenes onto the narration
 * word the decision model picks for it, so the agent no longer spends turns choosing `start_words`
 * itself. A scene's heading stays at the start, and an icon lands with the text it illustrates. All
 * assets go to the model in one batched request (a few for very long videos). Writes go through the
 * same path as set_text/set_icon, so the group reveal-order rules apply: texts are applied before
 * icons, each in tree order, so earlier group items land first.
 *
 * A landing that would trip another lint check is held back instead, with the reason for the
 * report: a text revealed too late in its scene to be read (voicedriven_scene_reading_time), or an
 * overlay left with nothing on screen at its start (late_scene_content). Fixing either means
 * re-timing the scene, which is the agent's call, not this pass's.
 */
export async function autoTimeAssets({
    luminary,
    dispatch,
    getState,
    parentActionGroup,
    ownsSceneGroup = () => true,
}: {
    luminary: Luminary;
    dispatch: AppDispatch;
    getState: GetRootState;
    parentActionGroup: string;
    ownsSceneGroup?: (sceneGroup: SceneGroup) => boolean;
}): Promise<AutoTimingResult> {
    const candidates = collectCandidates(luminary, getState, ownsSceneGroup);
    if (candidates.length === 0) {
        return { timed: [], held: [] };
    }
    const { answers, asked } = await askDecisionModel(luminary, candidates);

    const { resolveAssetTiming, applyResolvedAssetTiming } = makeOperationsCallable(
        { resolveAssetTimingOperation, applyResolvedAssetTimingOperation },
        luminary,
        dispatch,
        getState,
    );
    // The model agreeing with where the asset already lands (usually the scene's first word) is
    // not a change worth writing or reporting.
    const landingOn = (candidate: Candidate, startWords: string[]): Landing | null => {
        const resolved = resolveAssetTiming(candidate.scene, candidate.asset, {
            start_words: startWords,
        });
        if (
            'error' in resolved ||
            Math.abs(resolved.introTime - candidate.asset.introAnimationOffset) <=
                UNCHANGED_TOLERANCE_MS
        ) {
            return null;
        }
        return { candidate, startWords, introTime: resolved.introTime };
    };

    const timed = candidates.map((candidate, index) => ({ ...candidate, ...answers[index] }));
    const pickedWord = ({ words, word }: (typeof timed)[number]): number | null => {
        const wordIndex = Number(word?.choice);
        // A pick the model saw as identical to another option ("a [a] a") is a guess between them.
        const options = wordOptions(words);
        return words[wordIndex] &&
            options.indexOf(options[wordIndex]) === options.lastIndexOf(options[wordIndex])
            ? wordIndex
            : null;
    };
    const illustratedText = ({ scene, relation }: (typeof timed)[number]): Asset | undefined =>
        sceneTexts(scene).find((text) => text.name === relation?.choice);

    // An icon and the text it illustrates introduce one thing, so the pair lands on whichever of
    // them the narration reaches first — the label may paraphrase what the icon names outright.
    const iconPicksByText = new Map<Asset, number[]>();
    // Only an icon the library describes has a word of its own; an undescribed one's pick is a guess.
    const describedIcons = timed.filter(
        ({ asset }) =>
            VideoData.isIconAsset(asset) && Boolean((asset.sourceData as LibraryIconSource).title),
    );
    for (const candidate of describedIcons) {
        const text = illustratedText(candidate);
        const wordIndex = pickedWord(candidate);
        if (text && wordIndex !== null) {
            iconPicksByText.set(text, [...(iconPicksByText.get(text) ?? []), wordIndex]);
        }
    }

    const landings = new Map<Asset, Landing>();
    const held: HeldAsset[] = [];
    const hold = ({ candidate }: Landing, reason: string) => {
        landings.delete(candidate.asset);
        if (asked.has(candidate.asset)) {
            held.push({ sceneId: candidate.scene.id, assetName: candidate.asset.name, reason });
        }
    };
    const readingSpeed = videoReadingSpeed(getState());

    const textsFirst = [...timed].sort(
        (a, b) => Number(VideoData.isIconAsset(a.asset)) - Number(VideoData.isIconAsset(b.asset)),
    );
    // Texts that are candidates themselves, so an icon waits on where its text lands.
    const candidateTexts = new Set(timed.map(({ asset }) => asset));
    for (const candidate of textsFirst) {
        const { scene, asset, words, tokens, relation } = candidate;
        let landing: Landing | null = null;
        const text = VideoData.isIconAsset(asset) ? illustratedText(candidate) : undefined;
        if (text && candidateTexts.has(text)) {
            const textLanding = landings.get(text);
            landing = textLanding ? landingOn(candidate, textLanding.startWords) : null;
        } else {
            const isHeading = VideoData.isTextAsset(asset) && relation?.choice === HEADING;
            const picks = [pickedWord(candidate), ...(iconPicksByText.get(asset) ?? [])].filter(
                (pick): pick is number => pick !== null,
            );
            landing =
                isHeading || picks.length === 0
                    ? null
                    : landingOn(candidate, uniqueStartWords(tokens, words[Math.min(...picks)]));
        }
        if (!landing) {
            continue;
        }
        landings.set(asset, landing);
        // Only a move later can make text harder to read: one earlier gives it longer on screen than
        // where it sits now, even if still not long enough.
        if (VideoData.isTextAsset(asset) && landing.introTime > asset.introAnimationOffset) {
            const visibleMs = revealedTextVisibleMs(
                scene,
                asset.luminaryParent?.duration ?? scene.duration,
                landing.introTime,
            );
            if (isTooBriefToRead(asset.text, visibleMs, readingSpeed)) {
                hold(
                    landing,
                    `on "${landing.startWords.join(' ')}" it would be on screen for only ` +
                        `${seconds(Math.max(0, visibleMs))} before the scene ends, too briefly to read. ` +
                        'To time it there, first extend the scene over more of the narration ' +
                        `(${TOOL_NAMES.setSceneRange}).`,
                );
            }
        }
    }

    // An overlay that opened on content must still open on it. Hold back whatever would land first
    // until something is on screen from the start; an icon goes with the text it lands with.
    const overlays = new Set(
        [...landings.values()]
            .map(({ candidate }) => candidate)
            .filter(({ isOverlay, scene }) => isOverlay && !isMotionGraphicsScene(scene))
            .map(({ scene }) => scene),
    );
    for (const scene of overlays) {
        const content = editableContentAssets(scene).filter(hasContent);
        if (
            content.length === 0 ||
            Math.min(...content.map(appearsAtInScene)) > LATE_CONTENT_THRESHOLD_MS
        ) {
            continue;
        }
        const appearsAt = (asset: (typeof content)[number]) =>
            landings.get(asset as unknown as Asset)?.introTime ?? appearsAtInScene(asset);
        while (Math.min(...content.map(appearsAt)) > LATE_CONTENT_THRESHOLD_MS) {
            const first = [...landings.values()]
                .filter(({ candidate }) => candidate.scene === scene)
                .reduce((earliest, landing) =>
                    landing.introTime < earliest.introTime ? landing : earliest,
                );
            const reason =
                `on "${first.startWords.join(' ')}" nothing would be on screen for the first ` +
                `${seconds(first.introTime)} of the scene. If it should wait for that word, start ` +
                `the scene there with ${TOOL_NAMES.setSceneRange} instead.`;
            [...landings.values()]
                .filter(
                    ({ startWords, candidate }) =>
                        candidate.scene === scene && startWords === first.startWords,
                )
                .forEach((landing) => hold(landing, reason));
        }
    }

    return {
        timed: textsFirst.flatMap(({ scene, asset }): AutoTimedAsset[] => {
            const landing = landings.get(asset);
            if (!landing) {
                return [];
            }
            const args = { start_words: landing.startWords };
            // Re-resolved now that earlier assets in its group have landed: the reveal-order rules
            // can hold it back behind them.
            const resolved = resolveAssetTiming(scene, asset, args);
            if (
                'error' in resolved ||
                Math.abs(resolved.introTime - asset.introAnimationOffset) <= UNCHANGED_TOLERANCE_MS
            ) {
                return [];
            }
            const applied = applyResolvedAssetTiming(
                scene,
                asset,
                resolved,
                args,
                parentActionGroup,
            );
            return 'change' in applied
                ? [{ sceneId: scene.id, assetName: asset.name, startWords: landing.startWords }]
                : [];
        }),
        held,
    };
}
